import { mergeStyleSets } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { $cloud30 } from "../../../cssjs/colors";
import { useApiCall } from "../../../hooks/useApiCall";
import IPaxSettings, {
  ITouchPoint,
} from "../../../models/app-settings/ITouchPoint";
import { getPaxSettings } from "../../../store/pax-touchpoints/PaxSettingsSelector";
import { paxSettingActions } from "../../../store/pax-touchpoints/PaxSettingsSlice";
import { useAppDispatch, useAppSelector } from "../../../store/StoreHooks";
import { AIRPORT_CODE_PARAMETER } from "../../../utils/common/Constants";
import { LoadingScreen } from "../../common/loading-screen/LoadingScreen";
import ErrorMessage from "../../common/message/ErrorMessage";
import { AmdTabs, ITab } from "../../common/tabs/tabs";
import { PaxTouchPoints } from "./PaxTouchPoints";

//Define the base URL and the Pax Touchpoints URL
const paxURL = process.env.REACT_APP_API_PAX_TOUCHPOINTS;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const url = `${baseURL}${paxURL}`;

//PaxSettings Component to display the Passenger Flow Touchpoints
export const PaxSettings: React.FunctionComponent<IPaxSettingsProps> = (
  props: IPaxSettingsProps
) => {
  const dispatch = useAppDispatch();

  const urlWithParams = url?.replace(AIRPORT_CODE_PARAMETER, props.airportCode);

  const { airportTouchPoints, terminalsTouchPoints } = useAppSelector((state) =>
    getPaxSettings(state)
  );

  const refreshPaxSettings = airportTouchPoints.length === 0;

  const {
    data: paxFlowTouchpoints,
    error,
    isLoading,
  } = useApiCall<IPaxSettings>({
    url: urlWithParams,
    refresh: refreshPaxSettings,
    dependencies: [props.airportCode],
  });

  useEffect(() => {
    if (paxFlowTouchpoints?.airport) {
      dispatch(
        paxSettingActions.setPaxTouchpoints({
          airportTouchPoints: paxFlowTouchpoints?.touchPoints,
          terminalsTouchPoints: paxFlowTouchpoints?.terminals,
        })
      );
    }
  }, [paxFlowTouchpoints, dispatch]);

  //Extract the Terminals
  const terminals = terminalsTouchPoints.map((terminal) => {
    return { text: terminal.name, value: terminal.name };
  });
  const paxTabs: ITab[] = [
    { text: "Airport", value: "Airport" },
    ...(terminals || []),
  ];

  //Set the default selected Tab
  const firstPaxTab = paxTabs[0].value;
  const [selectedTab, setSelectedTab] = useState<string>(firstPaxTab);

  //Drop Down Tab select handler
  const onTabSelect = (e: any) => {
    setSelectedTab(e);
  };

  const onSaveHandler = () => {
    //Implement Save button logic here
    console.log("Save function called");
  };

  //Extract the Touchpoints as per the selected Tab
  const touchPointpaxFlowTouchpoints: ITouchPoint[] | undefined =
    selectedTab === "Airport"
      ? (airportTouchPoints ?? [])
      : (terminalsTouchPoints.find((terminal) => terminal.name === selectedTab)
          ?.touchPoints ?? []);

  return (
    <article data-testid="pax-settings">
      {error && (
        <div className="py-sm-1 ma-2" data-testid="pax-error-screen">
          <ErrorMessage message={error} />
        </div>
      )}
      {isLoading && (
        <div
          className={contentStyles.loadingScreen}
          data-testid="pax-loading-screen"
        >
          <LoadingScreen
            message={"Loading..."}
            spinClassName={contentStyles.spinner}
            labelClassName={contentStyles.loadingMessage}
          />
        </div>
      )}
      {!error && !isLoading && (
        <>
          <div data-testid="pax-settings-tabs-content">
            <AmdTabs
              defaultSelectedTab={firstPaxTab}
              tabList={paxTabs}
              dataTestId="pax-settings-tabs"
              onTabClick={onTabSelect}
              selectedTab={selectedTab}
            />
            {touchPointpaxFlowTouchpoints && (
              <PaxTouchPoints
                touchPoints={touchPointpaxFlowTouchpoints}
                selectedTab={selectedTab}
              />
            )}
          </div>
          <footer className="mt-4 mb-2">
            <Button
              onClick={onSaveHandler}
              appearance="primary"
              className="ml-2"
            >
              Save
            </Button>
          </footer>
        </>
      )}
    </article>
  );
};

export interface IPaxSettingsProps {
  airportCode: string;
}

const contentStyles = mergeStyleSets({
  headerStyles: {
    height: "50px",
    borderBottom: `1.5px solid ${$cloud30}`,
    marginBottom: "-8px",
  },
  spinner: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    color: "grey !important",
  },
  loadingMessage: {
    fontSize: "24px",
    color: "grey",
    fontStyle: "italic",
  },
  loadingScreen: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "150px",
  },
});
